import { useEffect, useMemo, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  CircleMarker,
  Tooltip as LeafletTooltip,
  useMap,
} from 'react-leaflet';
import { LatLngBoundsExpression, LatLngTuple, divIcon } from 'leaflet';
import { Box, Typography } from '@material-ui/core';

import api from '../../../../services/api';

interface ILiveRoutePosition {
  routeId: number;
  routeName: string;
  driverName: string | null;
  delivered: number;
  undelivered: number;
  totalStops: number;
  minutesSinceSync: number | null;
  situation: string;
  position: { lat: number; long: number } | null;
}

interface IRegionalCity {
  city: string;
  state: string | null;
  lat: number | null;
  long: number | null;
  stops: number;
  delivered: number;
  undelivered: number;
  undeliveredRate: number;
  topReason: string | null;
  lateOrders: number;
}

const situationColor = (situation: string) => {
  if (situation === 'no-signal') return '#B26A00';
  if (situation === 'late') return '#C62828';
  return '#2E7D32';
};

const rateColor = (rate: number) => {
  if (rate >= 10) return '#C62828';
  if (rate >= 5) return '#B26A00';
  return '#2E7D32';
};

const FitBounds = ({ points }: { points: LatLngTuple[] }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(points as LatLngBoundsExpression, { padding: [40, 40] });
    }
  }, [map, points]);
  return null;
};

/**
 * Mapa do painel: motoristas ao vivo (posição do app Driver) e camada de
 * análise regional (círculos por cidade, cor pela taxa de não-entrega).
 */
export const LiveOperationsMap = ({
  routes,
  showRegional,
}: {
  routes: ILiveRoutePosition[];
  showRegional: boolean;
}) => {
  const [regional, setRegional] = useState<IRegionalCity[]>([]);

  useEffect(() => {
    if (!showRegional) return;
    api
      .get('/summarizers/regional-analysis?days=30')
      .then((response) => setRegional(response.data?.cities || []))
      .catch(() => setRegional([]));
  }, [showRegional]);

  const driverPoints = useMemo(
    () =>
      routes.filter(
        (route): route is ILiveRoutePosition & {
          position: { lat: number; long: number };
        } => route.position !== null
      ),
    [routes]
  );

  const allPoints = useMemo(() => {
    const points: LatLngTuple[] = driverPoints.map((route) => [
      route.position.lat,
      route.position.long,
    ]);
    if (showRegional) {
      regional.forEach((city) => {
        if (city.lat !== null && city.long !== null) {
          points.push([city.lat, city.long]);
        }
      });
    }
    return points;
  }, [driverPoints, regional, showRegional]);

  if (allPoints.length === 0) {
    return (
      <Box p={3}>
        <Typography style={{ color: '#757575', fontSize: 13 }}>
          As posições aparecem aqui quando os motoristas estiverem usando o
          app 4LogX Driver (o app envia a localização junto da sincronização).
        </Typography>
      </Box>
    );
  }

  return (
    <MapContainer
      center={allPoints[0]}
      zoom={9}
      scrollWheelZoom
      style={{ width: '100%', height: 420 }}
    >
      <TileLayer
        attribution='&copy; <a href="https://4innovation.co/">4INNOVATION TECNOLOGIA</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds points={allPoints} />

      {showRegional &&
        regional.map(
          (city) =>
            city.lat !== null &&
            city.long !== null && (
              <CircleMarker
                key={`${city.city}-${city.state}`}
                center={[city.lat, city.long]}
                radius={Math.min(28, 8 + Math.sqrt(city.stops) * 2)}
                pathOptions={{
                  color: rateColor(city.undeliveredRate),
                  fillColor: rateColor(city.undeliveredRate),
                  fillOpacity: 0.25,
                  weight: 2,
                }}
              >
                <LeafletTooltip direction="top" opacity={1}>
                  <b>
                    {city.city}
                    {city.state ? ` - ${city.state}` : ''}
                  </b>
                  <br />
                  {city.stops} paradas · {city.undelivered} não realizadas (
                  {city.undeliveredRate}%)
                  {city.topReason && (
                    <>
                      <br />
                      Motivo nº1: {city.topReason}
                    </>
                  )}
                  {city.lateOrders > 0 && (
                    <>
                      <br />
                      {city.lateOrders} pedido(s) atrasado(s)
                    </>
                  )}
                </LeafletTooltip>
              </CircleMarker>
            )
        )}

      {driverPoints.map((route) => (
        <Marker
          key={route.routeId}
          position={[route.position.lat, route.position.long]}
          icon={divIcon({
            className: '',
            html: `<div style="width:22px;height:22px;border-radius:50%;
              background:${situationColor(route.situation)};
              border:3px solid #FFF;box-shadow:0 1px 6px rgba(0,0,0,.4)"></div>`,
            iconSize: [22, 22],
            iconAnchor: [11, 11],
            tooltipAnchor: [0, -12],
          })}
        >
          <LeafletTooltip direction="top" opacity={1}>
            <b>{route.routeName}</b>
            <br />
            {route.driverName || 'Motorista'} ·{' '}
            {route.delivered + route.undelivered}/{route.totalStops} paradas
            {route.minutesSinceSync !== null && (
              <>
                <br />
                sync há {route.minutesSinceSync} min
              </>
            )}
          </LeafletTooltip>
        </Marker>
      ))}
    </MapContainer>
  );
};
